<script setup>
// Os iframes dos aplicativos (Integrações → Painel de Aplicativos), vivos
// enquanto o painel está aberto. Cada um fica escondido até um
// PalcoDoAplicativo pedir; aí ele é posto por cima da área do palco (posição
// fixa, relida a cada quadro enquanto está à vista, porque o menu e o painel
// do contato mudam o tamanho sem aviso). Mover um iframe de lugar no DOM o
// recarregaria; por isso ele não sai daqui.
//
// O contexto da conversa vai para o site como no Chatwoot
// (postMessage 'appContext'), para quem usa a API de aplicativos.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { aplicativos, enderecoDe } from './estado';

const PEDIDO_DE_CONTEXTO = 'chatwoot-dashboard-app:fetch-info';

const store = useStore();
const registros = computed(() => store.getters['dashboardApps/getRecords']);
const abertos = computed(() =>
  aplicativos.abertos
    .map(id => registros.value.find(app => app.id === id))
    .filter(Boolean)
);

const caixa = ref(null);
let quadro = null;
const mede = () => {
  const alvo = aplicativos.alvo;
  if (alvo?.isConnected) {
    const { left, top, width, height } = alvo.getBoundingClientRect();
    const atual = caixa.value;
    if (
      !atual ||
      atual.left !== left ||
      atual.top !== top ||
      atual.width !== width ||
      atual.height !== height
    ) {
      caixa.value = { left, top, width, height };
    }
  }
  quadro = requestAnimationFrame(mede);
};
watch(
  () => aplicativos.ativo,
  ativo => {
    cancelAnimationFrame(quadro);
    caixa.value = null;
    if (ativo) mede();
  }
);

const estilo = id => {
  if (aplicativos.ativo !== id || !caixa.value) return { display: 'none' };
  const { left, top, width, height } = caixa.value;
  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
  };
};

// ---------- contexto da conversa ----------
const quadros = {};
const contexto = () => {
  const conversa = store.getters.getSelectedChat || {};
  const contatoId = conversa?.meta?.sender?.id;
  const { id, name, email } = store.getters.getCurrentUser || {};
  return {
    conversation: conversa,
    contact: contatoId ? store.getters['contacts/getContact'](contatoId) : {},
    currentAgent: { id, name, email },
    customAttributes: store.getters['attributes/getAttributes'],
    theme: document.body.classList.contains('dark') ? 'dark' : 'light',
  };
};
const enviaContexto = id => {
  const janela = quadros[id]?.contentWindow;
  if (!janela) return;
  janela.postMessage(
    JSON.stringify({ event: 'appContext', data: contexto() }),
    '*'
  );
};
watch(
  () => [aplicativos.ativo, store.getters.getSelectedChat?.id],
  ([ativo]) => {
    if (ativo) enviaContexto(ativo);
  }
);
const aoReceber = evento => {
  if (evento.data !== PEDIDO_DE_CONTEXTO) return;
  const id = Object.keys(quadros).find(
    chave => quadros[chave]?.contentWindow === evento.source
  );
  if (id) enviaContexto(Number(id));
};
onMounted(() => window.addEventListener('message', aoReceber));
onBeforeUnmount(() => {
  window.removeEventListener('message', aoReceber);
  cancelAnimationFrame(quadro);
});
</script>

<template>
  <div>
    <iframe
      v-for="app in abertos"
      :key="`${app.id}-${aplicativos.recomecos[app.id] || 0}`"
      :ref="elemento => (quadros[app.id] = elemento)"
      :src="enderecoDe(app)"
      :title="app.title"
      class="fixed z-10 bg-white border-0"
      :style="estilo(app.id)"
      allow="clipboard-read; clipboard-write; fullscreen"
      @load="enviaContexto(app.id)"
    />
  </div>
</template>
